/* global debounce */

if (!customElements.get('typesense-search')) {
  // const COLLECTION_SUFFIX = 'prod';
  const COLLECTION_SUFFIX = window.theme.typesense.collectionSuffix;
  const COLLECTIONS = {
    suggestions: `product_queries_${COLLECTION_SUFFIX}`,
    collections: `collections_${COLLECTION_SUFFIX}`,
    products: `products_${COLLECTION_SUFFIX}`,
    pages: `pages_${COLLECTION_SUFFIX}`,
  };

  class TypesenseSearch extends HTMLElement {
    constructor() {
      super();
      this.storageAttribute = "sportsfuel:scrollID";
      this.isSearchPage = window.location.pathname.includes("/search");
      this.collectionHandle = this.getCollectionHandle();
      this.query = this.getInitialQuery();
      this.queryEl = document.getElementById("search-query");
      this.previous = document.getElementById("show-previous");
      this.more = document.getElementById("show-more");
      this.statsTop = document.getElementById("ts-stats-top");
      this.statsBottom = document.getElementById("ts-stats-bottom");
      this.refinementList = document.getElementById("ts-refinement-list");
      this.openFilter = document.getElementById("ts-open-filter");
      this.closeFilter = document.getElementById("ts-close-filter");
      this.hitsEl = document.getElementById("hits");
      this.vendorFilter = document.getElementById("ts-vendor-filter");
      this.vendorFilterInput = this.vendorFilter.querySelector("input");
      this.vendorList = null;
      this.mobileListenersActive = false;
      this.vendorItems = [];
      this.client = new TypesenseInstantSearchAdapter({
        server: {
          apiKey: window.theme.typesense.apiKey,
          nodes: [
            {
              host: window.theme.typesense.url,
              port: '443',
              protocol: 'https'
            },
          ],
        },
        additionalSearchParameters: {
          preset: 'max-weight',
        },
        sortByOptions: {
          "isOutOfStock:asc,unifiedScore:desc": { enable_overrides: true, filter_curated_hits: true },
          "popularityScore:desc": { enable_overrides: false },
          "minPrice:asc": { enable_overrides: false },
          "minPrice:desc": { enable_overrides: false },
          "rating:desc": { enable_overrides: false },
          "publishedAt:desc": { enable_overrides: false }
        },
      }).searchClient;
    
      this.setupSearch(this.client);
      this.addListeners();
    }

    getInitialQuery() {
      if (this.isSearchPage) {
        return new URL(location.href).searchParams?.get('q');
      }

      return ''
    }

    getCollectionHandle() {
      const pathname = window.location.pathname

      if (pathname.includes('/collections/')) {
        const urlPart = pathname.split('/collections/')[1]
        const collectionHandle = urlPart?.split('?')[0]

        return collectionHandle || null
      }

      return null
    }

    updateQueryEl() {
      if (this.queryEl) {
        this.queryEl.innerHTML = this.query ? ` for "${this.query}"` : '';
      }
    }

    restoreScrollPosition() {
      const scrollID = sessionStorage.getItem(this.storageAttribute);
      
      if (scrollID) {
        const element = this.hitsEl.querySelector(`li[data-id="${scrollID}"]`);

        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        sessionStorage.removeItem(this.storageAttribute);
      }
    }

    addListeners() {
      this.setupRefinementListToggles();
      this.setupVendorFilter();
    }

    setupVendorFilter() {
      this.vendorFilterInput.addEventListener("input", e => {
        if (!this.vendorList) return;

        this.vendorList.innerHTML = `${this.renderRefinementListItems(this.getFilteredItems(this.vendorItems, e.target.value), "vendor")}`
      })
    }

    setupRefinementListToggles() {
      const refinementListTitleEls = this.refinementList.getElementsByClassName('ts-refinement-title');

      for (const el of refinementListTitleEls) {
        const parent = el.closest('.ts-refinement');
        const attribute = parent.dataset.id;

        if (attribute === 'vendor') {
          parent.dataset.expanded = true;
        }

        el.addEventListener("click", () => {
          if (parent.dataset.expanded === "true") {
            parent.dataset.expanded = false;
          } else {
            const current = this.refinementList.querySelector('[data-expanded="true"]');

            if (current) {
              current.dataset.expanded = false;
            }

            parent.dataset.expanded = true;
          }
        })
      }

      if (matchMedia("(max-width: 767px)").matches) {
        this.addMobileListeners();
      }

      addEventListener("resize", () => {
        if (this.mobileListenersActive && !matchMedia("(max-width: 767px)").matches) {
          this.removeMobileListeners();
        } else if (!this.mobileListenersActive && matchMedia("(max-width: 767px)").matches) {
          this.addMobileListeners();
        }
      });
    }

    onOpenFilter = () => {
      this.refinementList.dataset.open = "true";
      
      setTimeout(() => {
        this.refinementList.style.opacity = 1;
      }, 0);
    };

    onCloseFilter = () => {
      this.refinementList.style.opacity = 0;
        
      setTimeout(() => {
        this.refinementList.dataset.open = "false";
      }, 300);
    };

    onOutsideFilterClick = (e) => {
      if (e.target.tagName === 'ASIDE' && this.refinementList.dataset.open === "true") {
        this.onCloseFilter();
      }
    };

    addMobileListeners() {
      this.mobileListenersActive = true;
      this.openFilter.addEventListener("click", this.onOpenFilter);
      this.closeFilter.addEventListener("click", this.onCloseFilter);
      this.refinementList.addEventListener("click", this.onOutsideFilterClick);
    }

    removeMobileListeners() {
      this.mobileListenersActive = false;
      this.openFilter.removeEventListener("click", this.onOpenFilter);
      this.closeFilter.removeEventListener("click", this.onCloseFilter);
      this.refinementList.removeEventListener("click", this.onOutsideFilterClick);
      
      this.refinementList.style.opacity = 1;
      this.refinementList.dataset.open = "false";
    }

    async setupSearch(searchClient) {
      try {
        const search = instantsearch({
          searchClient,
          indexName: COLLECTIONS.products,
          routing: {
            stateMapping: {
              stateToRoute: uiState => {
                const collection = uiState[COLLECTIONS.products];
                const refinements = {};

                Object.entries(collection.refinementList || {}).forEach(refinement => {
                  if (!Array.isArray(refinement[1])) return;
                  
                  const key = refinement[0] === 'priceRanges' ? 'priceRanges' : refinement[0];
                  refinements[key] = refinement[1];
                })

                return {
                  q: collection.query,
                  sort_by: this.transformSortByToRoute(collection.sortBy),
                  page: collection.page ? parseInt(collection.page) : 1,
                  ...refinements,
                }
              },
              routeToState: routeState => {
                const { q, page, sort_by, ...refinements } = routeState;

                const refinementList = Object.entries(refinements).reduce((acc, refinement) => {
                  const facetAttributeName = refinement[0] === 'price_range' ? 'priceRanges' : refinement[0];
                  const attribute = [].concat(refinement[1]);

                  return {
                    ...acc,
                    [facetAttributeName]: attribute,
                  }
                }, {})
                
                return {
                  [COLLECTIONS.products]: {
                    query: q,
                    page: page ? parseInt(page) : 1,
                    refinementList,
                    sortBy: this.transformSortByToState(sort_by),
                  },
                }
              },
            },
          },
        });
        const {
          connectRefinementList,
          connectCurrentRefinements,
          connectSearchBox,
          connectInfiniteHits,
          connectSortBy,
          // connectQueryRules
        } = instantsearch.connectors
        const refinementList = connectRefinementList(this.renderRefinementList.bind(this));
        const refinements = connectCurrentRefinements(this.renderRefinements.bind(this));
        const searchbox = connectSearchBox(this.renderSearchBox.bind(this));
        const infiniteHits = connectInfiniteHits(this.renderInfiniteHits.bind(this));
        const sortBy = connectSortBy(this.renderSortBy.bind(this));

        search.addWidgets([
          instantsearch.widgets.configure({
            ...(this.collectionHandle ? { filters: `collections:=${this.collectionHandle}` } : {}),
            hitsPerPage: this.getHitsPerPage(this.collectionHandle)
          }),
          searchbox({}),
          refinements({
            container: document.getElementById('ts-refinements')
          }),
          refinementList({
            attribute: "vendor",
            container: document.querySelector('.ts-refinement[data-id="vendor"]'),
            limit: 1000,
            sortBy: ['name']
          }),
          refinementList({
            attribute: "productType",
            container: document.querySelector(`.ts-refinement[data-id="productType"]`),
            limit: 1000,
            sortBy: ['name']
          }),
          refinementList({
            attribute: "starRating",
            container: document.querySelector(`.ts-refinement[data-id="starRating"]`),
            transformItems: items => {
              return items.reduce((acc, item) => {
                if (Number(item.label) < 1) return [...acc, { ...item, label: 'No reviews', id: -Infinity }]

                return this.sortItemsByAttribute([...acc, { ...item, label: this.renderStarRating(Number(item.label)), id: item.label }], {
                  order: 'DESC',
                })
              }, [])
            }
          }),
          refinementList({
            attribute: "priceRanges",
            container: document.querySelector(`.ts-refinement[data-id="priceRanges"]`),
            limit: 25,
            transformItems: items => {
              const updatedItems = items.map(item => ({
                ...item,
                label: `$${item.label.replace('-', ' - $')}`,
                id: item.label.split('-')[0],
              }))

              return this.sortItemsByAttribute(updatedItems, {
                processValue: x => parseInt(x),
              })
            }
          }),
          refinementList({
            attribute: "dietaryPreferences",
            container: document.querySelector(`.ts-refinement[data-id="dietaryPreferences"]`),
            limit: 50,
            transformItems: items => this.sortItemsByLabel(items),
            operator: 'and'
          }),
          sortBy({
            container: document.getElementById('sort-by'),
            items: [
              { label: "Best Match", value: `${COLLECTIONS.products}/sort/isOutOfStock:asc,unifiedScore:desc` },
              { label: "Most Popular", value: `${COLLECTIONS.products}/sort/popularityScore:desc` },
              { label: "Price: Low to high", value: `${COLLECTIONS.products}/sort/minPrice:asc` },
              { label: "Price: High to low", value: `${COLLECTIONS.products}/sort/minPrice:desc` },
              { label: "Highest Rating", value: `${COLLECTIONS.products}/sort/weightedRating:desc` },
              { label: "Newest Arrivals", value: `${COLLECTIONS.products}/sort/publishedAt:desc` },
            ]
          }),
          infiniteHits({}),
        ]);

        search.start();

        if (this.isSearchPage) {
          this.updateQueryEl();
        }
      } catch (error) {
        throw error;
      }
    }

    renderRefinements(renderOptions, isFirstRender) {
      const { canRefine, items, refine, widgetParams: { container } } = renderOptions;

      if (isFirstRender) {
        container.addEventListener("click", function (e) {
          const { attribute, label, type, value } = e.target.dataset
          
          if (attribute && label && type && value) {
            refine({ attribute, label, type, value })
          }
        })
      }

      if (!items.length || !canRefine) {
        container.innerHTML = "";
        return;
      }

      container.innerHTML = `
        <ul class="ts-current-refinements">
          ${items.map(item => item.refinements.map(x => `
            <li data-attribute="${item.attribute}" data-label="${x.label}" data-type="${x.type}" data-value="${x.value}" class="ts-current-refinements__item">
              ${this.getRefinementLabel(item.attribute, x.label)}
              <svg class="gg-icon ts-current-refinements__icon" viewBox="0 0 256 256">
                <path class="gg-icon--stroke-3 gg-icon--stroke" d="M48 48l160 160M208 48L48 208"></path>
              </svg>
            </li>
          `).join('')).join('')}
        </ul>
      `
    }

    renderRefinementList(renderOptions, isFirstRender) {
      const { canRefine, items, refine, widgetParams: { attribute, container } } = renderOptions;

      if (isFirstRender) {
        const listEl = document.createElement('ul');
        
        if (attribute === "vendor") {
          this.vendorList = listEl;
        }

        listEl.addEventListener("click", (e) => {
          const itemEl = e.target.closest(".ts-items-list__item");

          if (!itemEl) return;

          refine(itemEl.querySelector('input')?.value);
        });
        
        listEl.className = "ts-items-list";
        container.querySelector(`.ts-items-list-wrapper`).appendChild(listEl);

      }
      
      if (!canRefine) {
        container.querySelector(`.ts-items-list`).innerHTML = '';
        return;
      }
      
      if (attribute === "vendor") {
        this.vendorItems = items;
        
        if (items?.length > 5) {
          container.querySelector(`.ts-refinement-searchbox`).style.display = "block";
        }
      }

      container.querySelector(`.ts-items-list`).innerHTML = `
        ${this.renderRefinementListItems(items, attribute)}
      `;
    };

    renderSortBy(renderOptions, isFirstRender) {
      const {
        canRefine,
        currentRefinement,
        options,
        refine,
        widgetParams,
      } = renderOptions;

      if (isFirstRender) {
        const select = document.createElement('select');
        select.className = 'ts-sort__select';
        select.setAttribute('id', 'ts-sort-by-select');
        select.addEventListener('change', event => refine(event.target.value));

        select.innerHTML = `${options.map(({ label, value }) => { 
          const isSelected = currentRefinement === value;

          return `
            <option ${isSelected ? "selected" : ""} value="${value}">
              ${label}
            </option>
          `
        }).join('')}`;

        widgetParams.container.appendChild(select);
      }

      const select = widgetParams.container.querySelector('select');

      select.disabled = !canRefine;
    };

    getRefinementLabel(attribute, label) {
      const stars = Number(label ?? 0)

      switch (attribute) {
        case 'priceRanges':
          return `$${label.replace('-', ' - $')}`;
        case 'rating':
        case 'weightedRating':
        case 'starRating':
          return stars > 0 ? `${stars} Star${stars !== 1 ? 's' : ''}` : 'No Reviews';
        default:
          return label;
      }
    }

    renderStatsText(nbVisibleHits, nbHits) {
      if (nbVisibleHits === 0) return `No results`;
      if (nbVisibleHits === 1) return `Showing 1 of 1 result`;

      return `Showing ${nbVisibleHits} of ${nbHits} results`;
    };

    isUsingDefaultIndex(currentIndex) {
      if (!currentIndex) return false;
      if (window.theme.typesense.sortByMostPopular === 'true' && this.collectionHandle === 'top-50') {
        return currentIndex === `${COLLECTIONS.products}/sort/popularityScore:desc`;
      }

      return currentIndex === COLLECTIONS.products || currentIndex === `${COLLECTIONS.products}/sort/isOutOfStock:asc,unifiedScore:desc`;
    }

    renderInfiniteHits(renderOptions, isFirstRender) {
      const { hits, isFirstPage, isLastPage, results, showMore, showPrevious } = renderOptions;
      const state = renderOptions.instantSearchInstance?.renderState[COLLECTIONS.products];
      const isDefaultIndex = this.isUsingDefaultIndex(state?.sortBy?.currentRefinement);
      const numbered = isDefaultIndex && (this.collectionHandle === 'top-50' || this.collectionHandle?.includes('best-selling-'));
      const userData = results?.userData?.length ? results.userData : null
      const promoBanners = userData?.reduce((acc, item) => item.promos?.length ? [...acc, ...item.promos] : acc, []) || null;
      
      if (isFirstRender) {
        this.previous.addEventListener("click", () => {
          showPrevious();
          this.onShowPrevious();
        });
        this.more.addEventListener("click", () => {
          showMore();
        });

        this.hitsEl.addEventListener("click", (e) => {
          e.preventDefault();
          
          if (e.target.tagName === "LI") return;
          
          const target = e.target.closest("a.gg-product");

          if (target) {
            sessionStorage.setItem(this.storageAttribute, target.getAttribute("data-id"));
            const hitJSON = target.querySelector('[type="application/json"]');
            const hit = hitJSON ? JSON.parse(hitJSON.textContent) : null;

            
            if (hit) {
              gg.sendEvent({
                data: {
                  hit,
                  position: target.getAttribute("data-position"),
                  index: COLLECTIONS.products
                },
                type: gg.eventTypes.CLICK
              });
            }
            
            if (e.metaKey || e.ctrlKey) {
              window.open(target.href, '_blank', 'noopener')
            } else {
              location.href = target.href;
            }
          } else {
            const promoTarget = e.target.closest('a.ts-search-results__promo-result');

            if (promoTarget) {
              gg.sendEvent({
                data: {
                  collectionHandle: this.collectionHandle,
                  title: promoTarget.name || ""
                },
                type: gg.eventTypes.PROMO_CLICK
              });

              if (e.metaKey || e.ctrlKey) {
              window.open(promoTarget.href, '_blank', 'noopener')
            } else {
              location.href = promoTarget.href;
            }
            }
          }
        });
      }

      if (hits?.length) {
        setTimeout(() => {
          this.restoreScrollPosition();
        }, 50);
      }

      const statsHTML = results
        ? `<div class="ts-hit-count">${this.renderStatsText(hits.length || 0, results?.nbHits || 0)}</div>`
        : `<div class="gg-skeleton-base gg-skeleton" style="width:16rem;"></div>`;

      this.statsTop.innerHTML = statsHTML;
      this.statsBottom.innerHTML = statsHTML;

      this.hitsEl.innerHTML = `
        ${results ? hits.map((hit, i) => {
          const isDiscontinued = hit.tags.includes('discontinued');
          const preorder = hit.tags.includes('preorder');
          const isOnSpecial = hit.compareAt && hit.compareAt > hit.minPrice;

          // TODO: sale label needs to be pulled into results somehow. Used to come from prismic sales.
          const saleLabel = null;
          const number = numbered ? hit.__position : null;
          const productLabel = hit.isOutOfStock || isDiscontinued ? null : this.getProductLabel(hit.tags);
          const ribbonType = this.getRibbonType(number ? 'number' : productLabel);
          const eventData = this.getEventDataFromHit(hit);
          const matchingPromos = promoBanners?.filter(({ positions }) => positions.includes(i + 1)) || null

          return `
            ${matchingPromos?.length ? this.renderPromoResults(matchingPromos) : ''}
            <li class="ts-search-results__result" data-id="${hit.id}">
              <a
                class="gg-product ${(hit.isOutOfStock || isDiscontinued) ? 'gg-product--out-of-stock' : ''}"
                data-id="${hit.id}"
                data-position="${i + 1}"
                href="/products/${hit.handle}"
              >
                <script type="application/json">${JSON.stringify(eventData)}</script>
                
                <div
                  class="gg-product__image-wrapper ${!hit.image?.src ? "gg-product__image-wrapper--placeholder" : ""} ${(hit.isOutOfStock || isDiscontinued) ? 'gg-product__image-wrapper--out-of-stock' : ''}"
                  style="padding-top:65%;"
                  ${number || productLabel ? `tag-type="${number ? 'number' : productLabel}"
                  tag-value="${number ? `#${number}` : productLabel}"` : ''}
                >
                  ${(number || productLabel) ? (
                    `<label class="gg-product__label ${number ? 'gg-product__label--number' : ''} gg-ribbon ${ribbonType ? `gg-ribbon--${ribbonType}` : ""}">
                      ${number || productLabel}
                    </label>`) : ""}
                  ${hit.image?.src ? `<img
                    alt="${hit.image?.alt || hit.title}"
                    class="gg-image gg-image--contain gg-image--absolute"
                    fit="contain"
                    loading="lazy"
                    src="${hit.image?.src}"
                  />` : this.getPlaceholderImageHtml()}
                </div>
                <div class="gg-product__info">
                  <div class="gg-product__title" title="${hit.title}">${hit.title}</div>
                  <div class="gg-product__type ${!hit.productType ? 'gg-product__type--hidden' : ''}">${hit.productType || ''}</div>
                  <div class="gg-product__options ${!hit.optionCounts?.length ? 'gg-product__options--hidden' : ''}">
                    ${hit.optionCounts?.length > 0 ? hit.optionCounts.map(({ key, value }) => `${value} ${key}`).join(' | ') : ''}
                  </div>
                  <div>
                    <div class="gg-product__prices">
                      <span class="gg-product__price ${isOnSpecial ? 'gg-product__price--highlighted' : ''} ${hit.maxPrice > hit.minPrice ? 'gg-product__price--from' : ''}">$${hit.minPrice.toFixed(2)}</span>
                      ${isOnSpecial ? `<span class="gg-product__compare-at">$${hit.compareAt.toFixed(2)}</span>` : ''}
                    </div>
                    ${this.renderTag({ isDiscontinued, isOnSpecial, isOutOfStock: hit.isOutOfStock, saleLabel, preorder })}
                  </div>
                  ${hit.starRating > 0 ? `
                    <div class="gg-product__rating">
                      <div class="gg-rating">
                        <div class="gg-rating__icons">
                          ${this.renderStarRating(hit.starRating)}
                        </div>
                      </div>
                      <span class="gg-product__review-count">(${hit.reviewCount} reviews)</span>
                    </div>
                  ` : ''}
                </div>
              </a>
            </li>
          `
        }).join('')
      : [...Array(4).keys()].map(() => `
          <li class="ts-search-results__result">
            <div class="gg-product">
              <div class="gg-product__image-wrapper">
                <div 
                  class="gg-skeleton-base gg-skeleton-image gg-product__image"
                  style="padding-top: 85%;"
                >
                  <svg
                    class="gg-skeleton-image-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 358.771 256"
                  >
                    <path d="M0 168c-6 64 50 80 104 80 86 0 132-33 141-82a67 67 0 001-14l-2-14c-19-55-135-42-130-70 2-12 13-17 29-17 7 0 14 2 18 6s6 9 6 17h39c18-56 50-66 102-66h40l-11 59c-30 0-41 0-45 37h39l-10 54h-39l-17 98h10l15-89h39l13-72h-39c2-9 4-14 7-16 5-3 14-3 27-3h8l14-76H148C99 0 34 18 24 77c-16 91 133 62 126 99-3 17-19 21-34 21-8 0-16-3-21-7-5-5-8-12-6-22z" />
                  </svg>
                </div>
              </div>
              <div class="gg-product__info">
                <div class="gg-product__title">
                  <div 
                    class="gg-skeleton-base gg-skeleton gg-skeleton-heading"
                  ></div>
                </div>
                <div>
                  <div class="gg-product__prices">
                    <div style="margin-bottom: 0.75rem; width: 50%;">
                      <div class="gg-skeleton-base gg-skeleton"></div>
                    </div>
                    <div class="gg-skeleton-base gg-skeleton"></div>
                  </div>
                  <div class="gg-product__rating">
                    <div class="gg-skeleton-base gg-skeleton"></div>
                  </div>
                </div>
              </div>
            </div>

          </li>
        `).join('')}
      `;

      this.more.style.display = "inline-block";

      if (isFirstPage) {
        this.previous.style.display = "none";
        this.previous.disabled = true;
      } else {
        this.previous.style.display = "inline-block";
        this.previous.disabled = false;
      }

      if (isLastPage) {
        this.more.textContent = "No more results";
        this.more.disabled = true;
      } else {
        this.more.textContent = "Show more";
        this.more.disabled = false;
      }
     
     if (hits?.length) {
        gg.sendEvent({
          hits: hits.map(hit => this.getEventDataFromHit(hit)),
          type: this.isSearchPage ? gg.eventTypes.VIEW_SEARCH_RESULTS : gg.eventTypes.VIEW_ITEM_LIST
        });
      }
    };

    getPlaceholderImageHtml() {
      return `
        <div class="gg-product__image">
          <svg
            class="gg-skeleton-image-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 358.771 256"
          >
            <path d="M0 168c-6 64 50 80 104 80 86 0 132-33 141-82a67 67 0 001-14l-2-14c-19-55-135-42-130-70 2-12 13-17 29-17 7 0 14 2 18 6s6 9 6 17h39c18-56 50-66 102-66h40l-11 59c-30 0-41 0-45 37h39l-10 54h-39l-17 98h10l15-89h39l13-72h-39c2-9 4-14 7-16 5-3 14-3 27-3h8l14-76H148C99 0 34 18 24 77c-16 91 133 62 126 99-3 17-19 21-34 21-8 0-16-3-21-7-5-5-8-12-6-22z" />
          </svg>
        </div>`
    }

    getEventDataFromHit(hit) {
      return {
        id: hit.id,
        title: hit.title,
        vendor: hit.vendor,
        productType: hit.productType,
        variant: "", // None available for impressions
        minPrice: hit.minPrice,
        variant_id: "", // None available for impressions
        compareAt: hit.compareAt || "",
        image: hit.image?.src ? {
          src: hit.image.src
        } : ""
      }
    }
      
    renderPromoResults(promos) {
      return promos.map(({ backgroundImageSrc, cssBackgroundColor, imageSrc, relativePath, title }) => `
        <li class="ts-search-results__result ts-search-results__result--promo ${relativePath ? 'ts-search-results__result--promo-with-link' : ''}">
          <div style="background: ${cssBackgroundColor}; position: relative; height: 100%;">
            ${backgroundImageSrc ? `
              <div class="ts-search-results__promo-bg-image">
                <img alt="${title}" class="gg-image gg-image--cover" src="${backgroundImageSrc}" />
              </div>
            ` : ''}
            <${relativePath ? 'a' : 'div'}
              class="ts-search-results__promo-result"
              ${relativePath ? `href="${relativePath}"` : ''}
              ${relativePath ? `name="${title}"` : ''}
            >
              <div class="ts-search-results__promo-image">
                <img alt="${title}" class="gg-image gg-image--contain" src="${imageSrc}" />
              </div>
            </${relativePath ? 'a' : 'div'}>
          </div>
        </li>
      `).join('');
    }

    renderSearchBox(renderOptions, isFirstRender) {
      // Nothing needs to be here but search box component is required
    }

    renderTag({ isDiscontinued, isOnSpecial, isOutOfStock, saleLabel, preorder }) {
      const prefix = `<div class="gg-product__tag"><span class="gg-tag gg-tag--pill gg-tag`;
      const suffix = `</span></div>`;

      if (isDiscontinued) return `${prefix}--dark">Discontinued${suffix}`;
      if (isOutOfStock) return `${prefix}--dark">Out Of Stock${suffix}`;
      if (saleLabel) return `${prefix}--primary">${saleLabel}${suffix}`;
      if (preorder) return `${prefix}--secondary">Pre-Order${suffix}`;
      if (isOnSpecial) return `${prefix}--primary">Special Price${suffix}`;
      
      return "";
    }

    renderStarRating(starRating) {
      return `<span class="rating__stars" aria-hidden="true" style="--rating: ${starRating};--rating-max: 5.0;">★★★★★</span>`
    }

    sortItemsByAttribute(items, options = {}, attribute = 'id') {
      const { order = 'ASC', processValue = x => x, type = 'NUM' } = options

      if (type === 'NUM') {
        return items.sort(
          (a, b) => (Number(processValue(a[attribute])) - Number(processValue(b[attribute]))) * (order === 'ASC' ? 1 : -1)
        )
      }

      return items.sort(
        (a, b) => processValue(a[attribute]).localeCompare(processValue(b[attribute])) * (order === 'ASC' ? 1 : -1)
      )
    }

    sortItemsByLabel(items) {
      return this.sortItemsByAttribute(
        items.map(item => ({ ...item, id: item.label })),
        {
          type: 'STRING',
        }
      )
    }
      

    transformSortByToRoute(state) {
      if (!state) return undefined;

      if (state.includes('unifiedScore')) return 'best_match';
      if (state.includes('popularityScore')) return 'popularity';
      if (state.includes('publishedAt')) return 'latest';
      if (state.includes('weightedRating')) return 'rating';

      return state.replace(`${COLLECTIONS.products}/sort/`, '');
    }

    transformSortByToState(state) {
      const prefix = `${COLLECTIONS.products}/sort/`;

      if (!state) {
        if (window.theme.typesense.sortByMostPopular === 'true') {
          return prefix + 'popularityScore:desc';
        }

        return undefined;
      }


      if (state.includes('best')) return prefix + 'isOutOfStock:asc,unifiedScore:desc';
      if (state.includes('popularity')) return prefix + 'popularityScore:desc';
      if (state.includes('latest')) return prefix + 'publishedAt:desc';
      if (state.includes('rating')) return prefix + 'weightedRating:desc';

      return prefix + state;
    }

    getHitsPerPage(handle = null) {
      if (!handle) return 48;

      if (handle.includes('top-50')) return 50;
      if (handle.includes('best-selling-')) return 10;

      return 24;
    };

    onShowPrevious() {
      const url = new URL(location);
      const page = url.searchParams?.get('page');
      const pageInt = page ? parseInt(page) : null

      if (pageInt > 1) {
        if (pageInt === 2) {
          url.searchParams.delete("page");
        } else {
          url.searchParams.set("page", pageInt - 1);
        }
        
        history.replaceState({}, "", url);
      }
    };

    getProductLabel = tags => {
      if (!tags?.length) return "";
      if (tags.includes('throwback-deal')) return 'throwback!';
      if (tags.includes('discontinued')) return 'discontinued';
      if (tags.includes('new_flavour')) return 'new flavour';
      if (tags.includes('new_look')) return 'new look';
      if (tags.includes('free_gift')) return 'free gift';
      if (tags.includes('is_stack')) return 'stack';
      if (tags.includes('new_product_label')) return 'new';
      if (tags.includes('hot_product_label')) return 'hot';

      return "";
    };
        
    getRibbonType(type) {
      if (type === 'free gift') return 'gift';
      if (type === 'new look' || type === 'new flavour') return 'hot';
      if (type === 'new' || type === 'add-on' || type === 'special') return 'new';

      return type || null;
    }

    renderRefinementListItems(items, attribute) {
      const filteredItems = attribute === "vendor" ? this.getFilteredItems(items, this.vendorFilterInput.value) : items;

      return filteredItems.map(item => {
        const disabled = item.count < 1;
        const isChecked = item.isRefined;

        return `
          <li class="ts-items-list__item ${attribute === "vendor" ? "ts-items-list__item--brand" : ""}">
            <label class="gg-checkbox ${disabled ? "checkbox--disabled" : ""} ts-items-list__checkbox">
              <input
                class="gg-hidden-input"
                ${isChecked ? "checked" : ""}
                type="checkbox"
                disabled="${disabled}"
                value="${item.value}"
              />
              <div class="gg-checkbox__content">
                <svg class="gg-icon gg-checkbox__icon ${disabled ? "gg-checkbox__icon--disabled" : ""}" viewBox="0 0 256 256" data-checked="${isChecked}">
                  <path class="gg-icon--stroke-3 gg-icon--stroke" d="M48 136l48 48L208 72"></path>
                </svg>
                <div class="gg-checkbox__label">
                  ${item.label} (${item.count || 0})
                </div>
              </div>
            </label>
          </li>
        `
      }).join("");
    }

    getFilteredItems(items, query) {
      const normalizedQuery = query.trim().toLowerCase();

      return items.filter(item => {
        return (
          typeof item.label !== 'string' ||
          item.label?.toLowerCase().includes(normalizedQuery) ||
          item.label
            ?.split(/\s+/)
            .map(s => s[0])
            .join('')
            .toLowerCase()
            .includes(normalizedQuery) ||
          item.label
            ?.split(/[^A-Z]+/)
            .join('')
            .toLowerCase()
            .includes(normalizedQuery)
        )
      })
    }
  }

  customElements.define('typesense-search', TypesenseSearch);
}
